import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { FieldTypeConfig, FormlyModule } from '@ngx-formly/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { injectDestroy } from 'ngxtension/inject-destroy';
import { FormlyErrorComponent } from '../formly-error.component';
import { FieldLabelRowComponent } from './field-label-row.component';
import { distinctUntilChanged, takeUntil } from 'rxjs';
import { DateHelperService } from '../../services/date-helper.service';
import { FormlyDataCyDirective } from '../../directives/formly-data-cy.directive';
import { ClearFieldButtonComponent } from './clear-field-button.component';
import { ClearableFieldType } from './clearable-field.type';

interface OpenableDatepicker {
  open(): void;
}

@Component({
  selector: 'formly-field-date',
  template: `
    <mat-form-field class="w-full" [class.mat-form-field-invalid]="formControl.invalid && formControl.touched">
      <mat-label>
        <app-field-label-row
          [label]="field.props.label + (field.props.required ? ' *' : '')"
          [required]="field.props.required"
          [info]="props.info"></app-field-label-row>
      </mat-label>
      <input
        #dateInput
        type="text"
        [formlyDataCy]="field"
        matInput
        [readonly]="isEmbedded"
        [formControl]="formControl"
        [formlyAttributes]="field"
        [matDatepicker]="picker"
        (click)="openPicker(dateInput, picker)" />
      <!-- Toggle and clear share one trailing cell. Filled + blurred shows clear; focusing the
           field restores the calendar toggle. Clearing opens the picker as prescribed. -->
      <span class="dd-clear-suffix dd-addon-suffix" matSuffix>
        <mat-datepicker-toggle class="dd-addon-suffix__passive" [for]="picker"></mat-datepicker-toggle>
        <app-clear-field-button
          [field]="field"
          [hasValue]="hasClearableValue()"
          [disabled]="isDisabled()"
          [ariaLabel]="clearAriaLabel()"
          [focusTarget]="dateInput"
          (clearSelection)="clearAndOpenPicker(dateInput, picker)" />
      </span>
      <mat-datepicker #picker [startAt]="formControl.value"></mat-datepicker>
      @if (field.props.description) {
        <mat-hint>{{ field.props.description }}</mat-hint>
      }
      <mat-hint>DD-MM-YYYY</mat-hint>
    </mat-form-field>
    <app-formly-error [field]="field"></app-formly-error>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    FormlyModule,
    MatInputModule,
    MatDatepickerModule,
    FormsModule,
    FormlyErrorComponent,
    FormlyDataCyDirective,
    ClearFieldButtonComponent,
    FieldLabelRowComponent,
  ],
})
export class FormlyFieldDateComponent extends ClearableFieldType<FieldTypeConfig> implements OnInit {
  isEmbedded = !!window.flutter_inappwebview;
  destroy$ = injectDestroy();
  constructor() {
    super();
  }

  ngOnInit() {
    this.field.formControl.valueChanges.pipe(takeUntil(this.destroy$), distinctUntilChanged()).subscribe(value => {
      if (value && value?.ts != undefined) {
        const date = new Date(value?.ts);
        this.formControl.setValue(DateHelperService.toLocalISOString(date), {
          emitEvent: false,
        });
      }
    });
  }

  // Open picker while maintaining input focus
  openPicker(input: HTMLInputElement, picker: OpenableDatepicker): void {
    picker.open();
    // Maintain focus on input after picker opens
    setTimeout(() => input.focus(), 32);
  }

  clearAndOpenPicker(input: HTMLInputElement, picker: OpenableDatepicker): void {
    super.clear();
    this.openPicker(input, picker);
  }
}
